import { useCallback, useRef } from 'react';
import { toastMessage } from '@ui/components/Toast';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export function usePdfExport() {
    const previewRef = useRef<HTMLDivElement>(null);
    const previewConsentRef = useRef<HTMLDivElement>(null);

    // PDF 다운로드 함수 (템플릿 미리보기용)
    const downloadTemplatePdf = useCallback(async (fileName: string) => {
        if (!previewRef.current) return;

        try {
            toastMessage({ message: 'PDF 생성 중...' });

            const canvas = await html2canvas(previewRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: '#ffffff',
            });

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;
            const contentHeight = pageHeight - margin * 2;

            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            if (imgHeight <= contentHeight) {
                const imgData = canvas.toDataURL('image/png');
                pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);
            } else {
                // 페이지 분할 시 글자 잘림 방지를 위한 오버랩 추가
                const overlapPixels = 20 * 2; // scale이 2이므로 20px * 2
                const pageHeightInPixels = (contentHeight / imgHeight) * canvas.height;
                const effectivePageHeight = pageHeightInPixels - overlapPixels;
                const totalPages = Math.ceil((canvas.height - overlapPixels) / effectivePageHeight);

                for (let page = 0; page < totalPages; page++) {
                    if (page > 0) {
                        pdf.addPage();
                    }

                    // 첫 페이지는 0부터, 이후 페이지는 오버랩을 고려하여 시작
                    const sourceY = page === 0 ? 0 : page * effectivePageHeight;
                    const sourceHeight = Math.min(pageHeightInPixels, canvas.height - sourceY);

                    const partCanvas = document.createElement('canvas');
                    partCanvas.width = canvas.width;
                    partCanvas.height = sourceHeight;
                    const partCtx = partCanvas.getContext('2d');

                    if (partCtx) {
                        // 흰색 배경 채우기
                        partCtx.fillStyle = '#ffffff';
                        partCtx.fillRect(0, 0, partCanvas.width, partCanvas.height);

                        partCtx.drawImage(
                            canvas,
                            0, sourceY, canvas.width, sourceHeight,
                            0, 0, canvas.width, sourceHeight
                        );

                        const partImgData = partCanvas.toDataURL('image/png');
                        const partImgHeight = (sourceHeight / canvas.height) * imgHeight;
                        pdf.addImage(partImgData, 'PNG', margin, margin, imgWidth, partImgHeight);
                    }
                }
            }

            pdf.save(`${fileName || '동의서'}.pdf`);
            toastMessage({ message: 'PDF가 다운로드되었습니다.', type: 'success' });
        } catch (error) {
            console.error('PDF 생성 실패:', error);
            toastMessage({ message: 'PDF 생성에 실패했습니다.', type: 'error' });
        }
    }, []);

    // PDF 다운로드 함수 (서명된 동의서용)
    const downloadConsentPdf = useCallback(async (fileName: string, signatureDataUrl?: string) => {
        if (!previewConsentRef.current) return;

        try {
            toastMessage({ message: 'PDF 생성 중...' });

            // 일반 이미지 로드 대기
            const images = previewConsentRef.current.querySelectorAll('img');
            await Promise.all(
                Array.from(images).map((img) => {
                    if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
                    return new Promise<void>((resolve) => {
                        img.onload = () => resolve();
                        img.onerror = () => resolve();
                    });
                })
            );

            // 서명 데이터 URL 및 위치 정보 가져오기
            let sigDataUrl = signatureDataUrl;
            let sigPosition: { x: number; y: number; width: number; height: number } | null = null;

            // 먼저 canvas 요소에서 서명 찾기
            const signatureCanvas = previewConsentRef.current.querySelector('canvas[data-signature-url]') as HTMLCanvasElement | null;

            if (signatureCanvas) {
                // canvas에서 직접 data URL 추출
                try {
                    sigDataUrl = signatureCanvas.toDataURL('image/png');
                } catch (e) {
                    sigDataUrl = signatureCanvas.getAttribute('data-signature-url') || signatureDataUrl;
                }

                // 실제 캔버스의 위치 계산 (previewConsentRef 기준)
                const containerRect = previewConsentRef.current.getBoundingClientRect();
                const canvasRect = signatureCanvas.getBoundingClientRect();

                sigPosition = {
                    x: canvasRect.left - containerRect.left,
                    y: canvasRect.top - containerRect.top,
                    width: canvasRect.width,
                    height: canvasRect.height,
                };
            } else {
                // canvas가 없으면 img 요소에서 서명 찾기
                const signatureImg = previewConsentRef.current.querySelector('img[alt="서명"]') as HTMLImageElement | null;

                if (signatureImg) {
                    // img 요소에서 src를 가져옴 (이미 signatureDataUrl로 전달받았을 수 있음)
                    if (!sigDataUrl && signatureImg.src) {
                        sigDataUrl = signatureImg.src;
                    }

                    // 이미지의 위치 계산 (previewConsentRef 기준)
                    const containerRect = previewConsentRef.current.getBoundingClientRect();
                    const imgRect = signatureImg.getBoundingClientRect();

                    sigPosition = {
                        x: imgRect.left - containerRect.left,
                        y: imgRect.top - containerRect.top,
                        width: imgRect.width,
                        height: imgRect.height,
                    };
                }
            }

            // 렌더링 안정화 대기
            await new Promise((resolve) => setTimeout(resolve, 500));

            // PDF 캡처 전 스크롤 가능 영역의 높이 제한 임시 제거
            const scrollableElements = previewConsentRef.current.querySelectorAll('[class*="overflow"], [class*="max-h-"]');
            const originalStyles: { element: HTMLElement; overflow: string; maxHeight: string; height: string }[] = [];

            scrollableElements.forEach((el) => {
                const htmlEl = el as HTMLElement;
                originalStyles.push({
                    element: htmlEl,
                    overflow: htmlEl.style.overflow,
                    maxHeight: htmlEl.style.maxHeight,
                    height: htmlEl.style.height,
                });
                htmlEl.style.overflow = 'visible';
                htmlEl.style.maxHeight = 'none';
                htmlEl.style.height = 'auto';
            });

            // 부모 요소도 스크롤 비활성화
            const originalContainerOverflow = previewConsentRef.current.style.overflow;
            const originalContainerMaxHeight = previewConsentRef.current.style.maxHeight;
            const originalContainerHeight = previewConsentRef.current.style.height;
            previewConsentRef.current.style.overflow = 'visible';
            previewConsentRef.current.style.maxHeight = 'none';
            previewConsentRef.current.style.height = 'auto';

            // 상태 배지 영역 임시 숨기기 (PDF에 불필요)
            const statusBadges = previewConsentRef.current.querySelectorAll('.bg-emerald-50.rounded-lg.border, .bg-amber-50.rounded-lg.border, .bg-blue-50.rounded-lg.border, .bg-rose-50.rounded-lg.border');
            const hiddenBadges: HTMLElement[] = [];
            statusBadges.forEach((badge) => {
                const htmlBadge = badge as HTMLElement;
                if (htmlBadge.style.display !== 'none') {
                    hiddenBadges.push(htmlBadge);
                    htmlBadge.style.display = 'none';
                }
            });

            const canvas = await html2canvas(previewConsentRef.current, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: '#ffffff',
                imageTimeout: 30000,
                scrollY: -window.scrollY,
                scrollX: -window.scrollX,
                onclone: (clonedDoc) => {
                    // 복제된 문서에서 추가 스타일 조정
                    const clonedContainer = clonedDoc.body.querySelector('[class*="overflow-y-auto"]');
                    if (clonedContainer) {
                        (clonedContainer as HTMLElement).style.overflow = 'visible';
                        (clonedContainer as HTMLElement).style.maxHeight = 'none';
                    }
                },
            });

            // 상태 배지 복원
            hiddenBadges.forEach((badge) => {
                badge.style.display = '';
            });

            // 원래 스타일 복원
            originalStyles.forEach(({ element, overflow, maxHeight, height }) => {
                element.style.overflow = overflow;
                element.style.maxHeight = maxHeight;
                element.style.height = height;
            });
            previewConsentRef.current.style.overflow = originalContainerOverflow;
            previewConsentRef.current.style.maxHeight = originalContainerMaxHeight;
            previewConsentRef.current.style.height = originalContainerHeight;

            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 10;
            const contentWidth = pageWidth - margin * 2;
            const contentHeight = pageHeight - margin * 2;

            // 원본 컨테이너 크기
            const containerRect = previewConsentRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const containerHeight = containerRect.height;

            const imgWidth = contentWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            // 스케일 계산 (DOM 픽셀 -> PDF mm)
            const scaleX = imgWidth / containerWidth;
            const scaleY = imgHeight / containerHeight;

            if (imgHeight <= contentHeight) {
                const imgData = canvas.toDataURL('image/png');
                pdf.addImage(imgData, 'PNG', margin, margin, imgWidth, imgHeight);

                // 서명 이미지 직접 추가 (실제 위치에)
                if (sigDataUrl && sigPosition) {
                    // DOM 위치를 PDF 위치로 변환 (캔버스의 정확한 위치 사용)
                    const sigX = margin + (sigPosition.x * scaleX);
                    const sigY = margin + (sigPosition.y * scaleY);
                    const sigWidth = sigPosition.width * scaleX;
                    const sigHeight = sigPosition.height * scaleY;

                    try {
                        pdf.addImage(sigDataUrl, 'PNG', sigX, sigY, sigWidth, sigHeight);
                    } catch (e) {
                        console.error('서명 이미지 추가 실패:', e);
                    }
                }
            } else {
                // 페이지 분할 시 글자 잘림 방지를 위한 오버랩 추가
                const overlapPixels = 20 * 2; // scale이 2이므로 20px * 2
                const pageHeightInPixels = (contentHeight / imgHeight) * canvas.height;
                const effectivePageHeight = pageHeightInPixels - overlapPixels;
                const totalPages = Math.ceil((canvas.height - overlapPixels) / effectivePageHeight);

                for (let page = 0; page < totalPages; page++) {
                    if (page > 0) {
                        pdf.addPage();
                    }

                    // 첫 페이지는 0부터, 이후 페이지는 오버랩을 고려하여 시작
                    const sourceY = page === 0 ? 0 : page * effectivePageHeight;
                    const sourceHeight = Math.min(pageHeightInPixels, canvas.height - sourceY);

                    const partCanvas = document.createElement('canvas');
                    partCanvas.width = canvas.width;
                    partCanvas.height = sourceHeight;
                    const partCtx = partCanvas.getContext('2d');

                    if (partCtx) {
                        // 흰색 배경 채우기
                        partCtx.fillStyle = '#ffffff';
                        partCtx.fillRect(0, 0, partCanvas.width, partCanvas.height);

                        partCtx.drawImage(
                            canvas,
                            0, sourceY, canvas.width, sourceHeight,
                            0, 0, canvas.width, sourceHeight
                        );

                        const partImgData = partCanvas.toDataURL('image/png');
                        const partImgHeight = (sourceHeight / canvas.height) * imgHeight;
                        pdf.addImage(partImgData, 'PNG', margin, margin, imgWidth, partImgHeight);
                    }

                    // 마지막 페이지에 서명 추가
                    if (page === totalPages - 1 && sigDataUrl) {
                        const sigWidth = 50;
                        const sigHeight = 15;
                        const sigX = pageWidth - margin - sigWidth - 10;
                        const partImgHeight = (sourceHeight / canvas.height) * imgHeight;
                        const sigY = margin + partImgHeight - sigHeight - 20;

                        try {
                            pdf.addImage(sigDataUrl, 'PNG', sigX, sigY, sigWidth, sigHeight);
                        } catch (e) {
                            console.error('서명 이미지 추가 실패:', e);
                        }
                    }
                }
            }

            pdf.save(fileName);
            toastMessage({ message: 'PDF가 다운로드되었습니다.', type: 'success' });
        } catch (error) {
            console.error('PDF 생성 실패:', error);
            toastMessage({ message: 'PDF 생성에 실패했습니다.', type: 'error' });
        }
    }, []);

    return {
        previewRef,
        previewConsentRef,
        downloadTemplatePdf,
        downloadConsentPdf,
    };
}
